import { useMemo } from 'react';

import { SUBSCRIPTION_STATUS } from '~/types/subscription';

const BLOCKED_TIME = 120; // 2 hours
const MILLISECONDS_IN_MINUTE = 1000 * 60;

const SUBSCRIPTION_STATUSES_WITH_DELETE_FLOW = [
  SUBSCRIPTION_STATUS.CANCELLED,
  SUBSCRIPTION_STATUS.CANCEL_PENDING,
];

const SUBSCRIPTION_STATUSES_WITH_VISIBLE_DELETE_BUTTON = [
  SUBSCRIPTION_STATUS.ACTIVE,
  ...SUBSCRIPTION_STATUSES_WITH_DELETE_FLOW,
];

export type DeleteAccountFlow = 'delete-account' | 'cancel-subscription';

type UseDeleteAccountBehaviorParams = {
  isAvailableForAllUsers: boolean;
  subscriptionStatus?: SUBSCRIPTION_STATUS;
  signUpAt: string | null;
};

type UseDeleteAccountBehaviorResult = {
  shouldShowDeleteAccountButton: boolean;
  accountFlow: DeleteAccountFlow;
};

export const shouldShowDeleteAccountButton = ({
  isAvailableForAllUsers,
  subscriptionStatus,
  signUpAt,
}: {
  isAvailableForAllUsers: boolean;
  subscriptionStatus?: SUBSCRIPTION_STATUS;
  signUpAt: string | null;
}): boolean => {
  if (isAvailableForAllUsers) {
    return true;
  }

  if (!subscriptionStatus) {
    return false;
  }

  if (
    !SUBSCRIPTION_STATUSES_WITH_VISIBLE_DELETE_BUTTON.includes(
      subscriptionStatus,
    )
  ) {
    return false;
  }

  if (signUpAt) {
    return (
      Math.abs(
        (Date.now() - new Date(signUpAt).getTime()) / MILLISECONDS_IN_MINUTE,
      ) > BLOCKED_TIME
    );
  }

  return true;
};

export const getDeleteAccountFlow = ({
  canDeleteWithoutActiveSubscription,
  subscriptionStatus,
}: {
  canDeleteWithoutActiveSubscription: boolean;
  subscriptionStatus?: SUBSCRIPTION_STATUS;
}): DeleteAccountFlow => {
  if (!subscriptionStatus) {
    return 'cancel-subscription';
  }

  if (canDeleteWithoutActiveSubscription) {
    return subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE
      ? 'cancel-subscription'
      : 'delete-account';
  }

  const canOpenDeleteFlow =
    SUBSCRIPTION_STATUSES_WITH_DELETE_FLOW.includes(subscriptionStatus);

  return canOpenDeleteFlow ? 'delete-account' : 'cancel-subscription';
};

const useDeleteAccountBehavior = ({
  isAvailableForAllUsers,
  subscriptionStatus,
  signUpAt,
}: UseDeleteAccountBehaviorParams): UseDeleteAccountBehaviorResult => {
  const shouldShowDeleteAccountButtonValue = useMemo(() => {
    return shouldShowDeleteAccountButton({
      isAvailableForAllUsers,
      subscriptionStatus,
      signUpAt,
    });
  }, [isAvailableForAllUsers, signUpAt, subscriptionStatus]);

  const accountFlow = useMemo(() => {
    return getDeleteAccountFlow({
      canDeleteWithoutActiveSubscription: isAvailableForAllUsers,
      subscriptionStatus,
    });
  }, [isAvailableForAllUsers, subscriptionStatus]);

  return {
    shouldShowDeleteAccountButton: shouldShowDeleteAccountButtonValue,
    accountFlow,
  };
};

export default useDeleteAccountBehavior;
